import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useLocation } from 'react-router-dom';

import savvyLogoBlack from '@/assets/svg/savvy-logo-black.svg';
import { requestProjectIllustration } from '@/assets/images';
import RequestSolutionIllustration from '@/assets/svg/request-solution-illustration.svg?react';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { Header, ParagraphLarge } from '@/ui/typography/widgets';
import { Flexbox, FlexContainer } from '@/ui/flexbox/flexbox';
import { Button } from '@/ui/button/button';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { modalState } from '@/state/modal.state';
import { Modals } from '@/modals';
import { useSetAtom } from 'jotai';
import { Stack } from '@/ui/line/line';

import { getSpotlightAccountDisplayName } from './get-spotlight-account-display-name';

interface SpotlightUnauthenticatedPageProps {
  linkId?: string;
  logoUrl: string;
  organizationName: string;
  organizationShortName?: string | null;
}

export const SpotlightUnauthenticatedPage = ({
  linkId,
  logoUrl,
  organizationName,
  organizationShortName: organizationShortNameFromLink,
}: SpotlightUnauthenticatedPageProps) => {
  const { t } = useTranslation('spotlight');
  const organizationDisplayName = getSpotlightAccountDisplayName({
    name: organizationName,
    shortName: organizationShortNameFromLink,
  });
  const navigateTo = useNavigateTo();
  const setModal = useSetAtom(modalState);
  const location = useLocation();
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState(
    logoUrl || savvyLogoBlack,
  );

  useEffect(() => {
    setResolvedLogoUrl(logoUrl || savvyLogoBlack);
  }, [logoUrl]);

  const handleLogoError = () => {
    setResolvedLogoUrl(savvyLogoBlack);
  };

  const handleSignIn = () => {
    const currentPath = `${location.pathname}${location.search}`;

    if (currentPath === '/') {
      navigateTo.signIn(undefined, undefined);
    } else {
      const redirectUrl = `redirectTo=${encodeURIComponent(currentPath)}`;
      navigateTo.signIn(undefined, redirectUrl);
    }
  };

  const handleAskToJoin = () => {
    setModal({
      linkId,
      organizationName: organizationDisplayName,
      spotlightLinkId: linkId,
      state: 'askToJoinForm',
    });
  };

  return (
    <WithHtmlSavvyTitle
      title={t('unauthenticated.title', {
        organizationName: organizationDisplayName,
      })}
    >
      <Container>
        <PageContainer>
          <HeroShell>
            <ContentGrid>
              <LeftSection>
                <LeftSectionContent>
                  <ContentSection>
                    <ContentStack direction="column" gap="24px">
                      <HeaderSection>
                        <LogoContainer>
                          <Logo
                            alt="Savvy logo"
                            onError={handleLogoError}
                            src={resolvedLogoUrl}
                          />
                        </LogoContainer>
                        <PageTitle>
                          {t('unauthenticated.title', {
                            organizationName: organizationDisplayName,
                          })}
                        </PageTitle>
                        <Subtitle>
                          {t('unauthenticated.subtitle', {
                            organizationName: organizationDisplayName,
                          })}
                        </Subtitle>
                      </HeaderSection>

                      <DescriptionSection>
                        <Description>{t`unauthenticated.description`}</Description>
                      </DescriptionSection>

                      <ButtonContainer>
                        <Button
                          onClick={handleSignIn}
                          textCenter
                          variant="primary"
                          width={90}
                        >
                          {t`unauthenticated.signIn`}
                        </Button>
                        <Button
                          onClick={handleAskToJoin}
                          textCenter
                          variant="secondary"
                          width={160}
                        >
                          {t`unauthenticated.askToJoin`}
                        </Button>
                      </ButtonContainer>
                    </ContentStack>
                  </ContentSection>

                  <FooterText>{t`unauthenticated.registeredUsersOnly`}</FooterText>
                </LeftSectionContent>
              </LeftSection>

              <RightSection>
                <IllustrationsStack
                  alignItems="flex-end"
                  direction="column"
                  fullHeight
                  fullWidth
                  justify="flex-end"
                >
                  <IllustrationContainer
                    alignItems="flex-end"
                    flex="1 1 0"
                    fullWidth
                    justify="center"
                  >
                    <DashboardIllustrationImage
                      alt="Request project illustration"
                      src={requestProjectIllustration}
                    />
                  </IllustrationContainer>
                  <SolutionIllustration
                    alignItems="flex-end"
                    flex="0 1 auto"
                    fullWidth
                    justify="flex-end"
                  >
                    <SolutionIllustrationSvg />
                  </SolutionIllustration>
                </IllustrationsStack>
              </RightSection>
            </ContentGrid>
          </HeroShell>
        </PageContainer>
      </Container>
      <Modals />
    </WithHtmlSavvyTitle>
  );
};

const Container = styled('div')`
  position: relative;
`;

const PageContainer = styled('div')`
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background-color: ${({ theme }) => theme.colors.gray.c1};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 42px 60px 42px 42px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 24px 32px 24px 24px;
    overflow-y: auto;
  }

  @media (max-width: 480px) {
    padding: 16px 24px 16px 16px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    align-items: flex-start;
    padding: 16px 32px 16px 24px;
    overflow-y: auto;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    max-height: 100vh;
    overflow: hidden;
    padding: 32px 48px 32px 40px;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    padding: 16px 32px 16px 24px;
  }
`;

const HeroShell = styled('div')`
  width: 100%;
  max-width: 1440px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  margin: 0 auto;

  @media (min-height: 921px) and (min-width: 769px) {
    height: 836px;
    max-height: 836px;
  }
`;

const ContentGrid = styled('div')`
  display: grid;
  grid-template-columns: minmax(480px, 1fr) minmax(280px, 480px);
  height: 100%;
  max-height: 100%;
  padding: 0;
  grid-gap: 28px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    height: auto;
    max-height: none;
    grid-gap: 24px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
    grid-gap: 16px;
  }

  @media (min-width: 769px) {
    min-height: 0;
    align-items: center;
  }
`;

const LeftSection = styled('div')`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  padding: 32px 112px 16px 48px;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  justify-content: center;

  @media (max-width: 1300px) and (min-width: 961px) {
    padding: 24px 64px 16px 32px;
  }

  @media (max-width: 960px) {
    padding: 32px 24px 16px 24px;
  }

  @media (max-width: 768px) {
    padding: 16px 8px;
    height: auto;
    max-height: none;
    overflow: visible;
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    padding: 8px 0;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 8px 16px;
    height: auto;
    max-height: none;
    overflow: visible;
    justify-content: flex-start;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    height: 100%;
    max-height: 100%;
    min-height: 0;
    justify-content: safe center;
    overflow: hidden;
    padding: 20px 56px 16px 36px;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    height: 100%;
    max-height: 100%;
    min-height: 0;
    justify-content: safe center;
    overflow: hidden;
    padding: 12px 32px 12px 28px;
  }
`;

const LeftSectionContent = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  flex: 0 1 auto;
  max-height: 100%;
  min-height: 0;

  @media (max-width: 768px) {
    flex: 1;
    gap: 24px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    flex: 1;
    gap: 12px;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    flex: 0 1 auto;
    gap: 18px;
    overflow-y: visible;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    gap: 10px;
    overflow-y: visible;
  }
`;

const ContentStack = styled(FlexContainer)`
  @media (min-height: 721px) and (min-width: 769px) {
    gap: 18px !important;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    gap: 8px !important;
  }
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 0;
  flex: 0 1 auto;
  overflow: visible;

  @media (max-width: 960px) {
    margin-bottom: 24px;
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
    max-width: 100%;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 0;
    max-width: 100%;
  }
`;

const HeaderSection = styled(Stack)`
  margin-bottom: 0;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    margin-bottom: 0;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 0;
  }

  @media (min-width: 769px) {
    margin-bottom: 0;
  }

  @media (max-height: 720px) and (min-width: 961px) {
    margin-bottom: 0;
  }
`;

const LogoContainer = styled('div')`
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 14px;
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 8px;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    margin-bottom: 16px;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    margin-bottom: 6px;
  }
`;

const PageTitle = styled(Header)`
  color: ${({ theme }) => theme.colors.basics.black};
  margin-bottom: 16px;
  font-size: 53px;
  font-weight: 600;
  line-height: 1.25;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 8px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 24px;
    margin-bottom: 6px;
    line-height: 1.2;
  }

  @media (min-height: 721px) and (min-width: 769px) and (max-width: 1300px) {
    font-size: 40px !important;
    line-height: 1.2 !important;
    margin-bottom: 12px;
  }

  @media (min-height: 721px) and (min-width: 1301px) {
    font-size: 36px !important;
    line-height: 1.18 !important;
    margin-bottom: 10px;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    font-size: 34px !important;
    line-height: 1.15 !important;
    margin-bottom: 4px;
  }
`;

const Subtitle = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.gray.c12};
  line-height: 1.6;
  font-size: 18px;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 13px;
    line-height: 1.4;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }
`;

const DescriptionSection = styled('div')`
  margin-bottom: 0;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    margin-bottom: 0;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 0;
  }

  @media (min-width: 769px) {
    margin-bottom: 0;
  }

  @media (max-height: 720px) and (min-width: 961px) {
    margin-bottom: 0;
  }
`;

const Description = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.gray.c12};
  line-height: 1.6;
  font-size: 18px;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 13px;
    line-height: 1.4;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  gap: 16px;
  height: 40px;
  margin-top: 4px;

  @media (min-height: 721px) and (min-width: 769px) {
    margin-top: 4px;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    margin-top: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    height: auto;
    gap: 12px;

    button {
      max-width: 100% !important;
      width: 100% !important;
    }
  }

  @media (max-height: 500px) and (orientation: landscape) and (min-width: 481px) {
    height: 36px;
    gap: 12px;

    button {
      height: 36px;
    }
  }
`;

const RightSection = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  text-align: left;

  @media (max-width: 960px) {
    display: none;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    display: none;
  }

  @media (min-width: 769px) {
    align-items: center;
    justify-content: center;
    min-height: 0;
  }
`;

const IllustrationsStack = styled(Flexbox)`
  max-height: 100%;
  min-height: 0;

  @media (min-width: 769px) {
    justify-content: center !important;
    max-height: 100%;
    min-height: 0;
  }
`;

const IllustrationContainer = styled(Flexbox)`
  min-height: 0;
  max-height: 58%;
  max-width: 460px;
  width: 100%;
`;

const DashboardIllustrationImage = styled('img')`
  max-width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
`;

const SolutionIllustration = styled(Flexbox)`
  min-height: 0;
  max-height: 42%;
  max-width: 420px;
  width: 100%;
`;

const SolutionIllustrationSvg = styled(RequestSolutionIllustration)`
  width: 100%;
  height: auto;
  max-height: 100%;
`;

const FooterText = styled('p')`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray.c11};
  margin: 0;
  padding: 0;
  line-height: 1.4;
  align-self: flex-start;
  flex-shrink: 0;
  opacity: 0.9;

  @media (max-width: 768px) {
    padding: 8px 0 0 0;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px 0 24px 0;
    font-size: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 8px 0 16px 0;
    font-size: 11px;
  }

  @media (min-height: 721px) and (min-width: 769px) {
    font-size: 13px;
    line-height: 1.35;
  }

  @media (max-height: 720px) and (min-width: 769px) {
    font-size: 13px;
    line-height: 1.35;
  }
`;

const Logo = styled('img')`
  height: 39px;
  width: auto;
`;
